import { SeverityNumber, type Logger } from '@opentelemetry/api-logs';
import type { ILoggerProvider } from '../../logging/types';
import type { TLogFields, TLogLevel } from '../../types';
import {
  flushOpenTelemetry,
  getOtelLogger,
  shutdownOpenTelemetry,
} from './initOpenTelemetry';

const SEVERITY: Record<TLogLevel, { number: SeverityNumber; text: string }> = {
  debug: { number: SeverityNumber.DEBUG, text: 'DEBUG' },
  info: { number: SeverityNumber.INFO, text: 'INFO' },
  warn: { number: SeverityNumber.WARN, text: 'WARN' },
  error: { number: SeverityNumber.ERROR, text: 'ERROR' },
};

/** Flatten fields (incl. `ctx`) → atributos OTel escalares. */
function toAttributes(
  fields?: TLogFields,
): Record<string, string | number | boolean> {
  if (!fields) return {};
  const out: Record<string, string | number | boolean> = {};

  for (const [key, value] of Object.entries(fields)) {
    if (value == null) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
      continue;
    }
    if (key === 'ctx' && typeof value === 'object' && !Array.isArray(value)) {
      for (const [ck, cv] of Object.entries(value as Record<string, unknown>)) {
        if (cv == null) continue;
        if (typeof cv === 'string' || typeof cv === 'number' || typeof cv === 'boolean') {
          out[`ctx.${ck}`] = cv;
        } else {
          out[`ctx.${ck}`] = String(cv);
        }
      }
      continue;
    }
    // ponytail: stringify nested/array — structured attrs se Loki exigir
    out[key] = String(value);
  }

  return out;
}

export class OtelLoggerProvider implements ILoggerProvider {
  private logger: Logger | null = null;

  private getLogger(): Logger {
    if (!this.logger) this.logger = getOtelLogger();
    return this.logger;
  }

  log(level: TLogLevel, message: string, fields?: TLogFields): void {
    const sev = SEVERITY[level];
    this.getLogger().emit({
      severityNumber: sev.number,
      severityText: sev.text,
      body: message,
      attributes: toAttributes(fields),
    });
  }

  async flush(): Promise<void> {
    await flushOpenTelemetry();
  }

  async shutdown(): Promise<void> {
    await shutdownOpenTelemetry();
  }
}
